import { format, subDays } from 'date-fns';
import type { Campaign } from 'c-sdk';
import { settledTarget, usesInsertionOrderTargeting } from 'c-main/Lib';
import type { SettledTarget } from 'c-main/Lib';

/**
 * UL-8133 -- target-based pacing for insertion-order targeting campaigns (`vistar_us2`).
 *
 * Every other DSP type paces `campaign.expected_impacts` evenly over the campaign. A `vistar_us2`
 * campaign has a target per insertion order instead, which the builder (UL-8132) spreads over each
 * IO's scheduled hours into `expected_impacts_in_schedule`. These helpers turn those rows into the
 * expected line, the expected bars and the headline. They are pure so the decisions can be tested
 * without rendering the analytics page (too large for a unit test).
 */

/**
 * Which targets can be shown. Targets are set per insertion order, not per screen, so they mean
 * something only while the filters are limited to insertion order and date.
 */
export type TargetFilterState = 'unfiltered' | 'insertionOrderOrDate' | 'otherFilter';

/** What `useCampaignAnalyticsWidgetData` needs to draw a `vistar_us2` campaign's targets. */
export type TargetSeriesParam = {
    /** `expected_impacts_in_schedule` by day through the campaign end; null until loaded. */
    dailyExpected: Record<string, number> | null;
    /** False under a filter targets cannot be split by (anything but insertion order / date). */
    showTargets: boolean;
};

/** The analytics filters that pick insertion orders (the IO is the "line item" in the PCA data). */
export const INSERTION_ORDER_FILTER_KEYS = ['display_line_item_name', 'display_line_item_id'];

/** Not user filters: the page sets these itself (the same exclusions as `hasUserActiveFilters`). */
const NOT_USER_FILTERS = ['display_type', 'scheduled'];

const hasValues = (value: unknown) =>
    Array.isArray(value) && value.some(v => v != null && v !== '');

export const targetFilterState = (
    advancedFilters: Record<string, unknown> | null | undefined,
    hasDateFilter: boolean,
): TargetFilterState => {
    const active = Object.entries(advancedFilters ?? {})
        .filter(([key, value]) => !NOT_USER_FILTERS.includes(key) && hasValues(value))
        .map(([key]) => key);

    if (active.some(key => !INSERTION_ORDER_FILTER_KEYS.includes(key))) return 'otherFilter';
    if (active.length > 0 || hasDateFilter) return 'insertionOrderOrDate';
    return 'unfiltered';
};

/**
 * Screen attributes: the grouping fields a `vistar_us2` target row has no value for. The builder
 * (UL-8132) writes each insertion order's target as rows with `frame_id = '0'` and no owner,
 * format, region or other screen detail, and the API leaves them out of any grouping by these
 * (UL-8204, `PCAStatsRepositoryAthena` `SCREEN_LEVEL_FIELDS` and `SCREEN_ATTRIBUTE_FIELDS`).
 */
export const SCREEN_ATTRIBUTE_FIELDS: readonly string[] = [
    // Screen level.
    'frame_id',
    'display_id',
    'display_screen_name_summary',
    'unit_id',
    'display_lat',
    'display_long',
    // Screen attributes.
    'display_owner',
    'display_owner_id',
    'display_owner_format',
    'display_format',
    'display_type',
    'display_env',
    'display_environment',
    'display_town',
    'display_conurbation',
    'display_tv_region',
    'display_resolution',
    'display_filetypes',
    'display_slots',
    'display_slot_duration',
    'display_sov',
    'display_surface_area',
];

/**
 * UL-8204 -- does this grouping hide a `vistar_us2` campaign's expected impressions?
 *
 * Targets are set per insertion order and can't be split by owner, format, region or screen, so
 * once the API leaves the target rows out of such a grouping every row's expected impressions is
 * 0. That is not a target of 0: the table shows "-" for Expected and % of target, and charts
 * draw no expected bars. Everything else is unchanged:
 * - plays and time keep their expected (they come from the schedule, not the targets);
 * - date, hour, day of week, insertion order (line item), creative and no grouping keep the
 *   target rows, so their expected impressions stand;
 * - every other campaign type, and `vistar_us2` under linear reporting (`isDsp` false).
 */
export const hidesExpectedImpressions = ({
    campaign,
    isDsp,
    metric,
    groupBy,
}: {
    campaign: Pick<Campaign, 'type'> | null | undefined;
    isDsp: boolean;
    /** `PCAReportMetric` / summary widget metric: only `'impacts'` is affected. */
    metric: string | null | undefined;
    /** Every field the rows are grouped by (one, or several for a multi-field grouping). */
    groupBy: readonly (string | null | undefined)[];
}): boolean =>
    isDsp &&
    usesInsertionOrderTargeting(campaign) &&
    metric === 'impacts' &&
    groupBy.some(field => field != null && SCREEN_ATTRIBUTE_FIELDS.includes(field));

/** The user's insertion-order filter, and nothing else, for the expected-series request. */
export const insertionOrderFieldFilters = (
    fieldFilters: Record<string, string[]> | null | undefined,
): Record<string, string[]> =>
    Object.fromEntries(
        Object.entries(fieldFilters ?? {}).filter(
            ([key, value]) => INSERTION_ORDER_FILTER_KEYS.includes(key) && hasValues(value),
        ),
    );

const DAY = 'yyyy-MM-dd';

/**
 * The expected series is asked for with explicit dates: a request with none returns settled rows
 * only (the API adds `inside_campaign_settings_date = '1'`), which would cut the line at the day
 * before yesterday. So it takes the campaign's own dates, or the user's date filter when set.
 */
export const targetExpectedWindow = ({
    campaignStart,
    campaignEnd,
    dateFilter,
}: {
    campaignStart: Date | null;
    campaignEnd: Date | null;
    dateFilter: { start: Date; end: Date } | null;
}): { startDate: string; endDate: string } | null => {
    if (dateFilter) {
        return { startDate: format(dateFilter.start, DAY), endDate: format(dateFilter.end, DAY) };
    }
    if (!campaignStart || !campaignEnd) return null;
    return { startDate: format(campaignStart, DAY), endDate: format(campaignEnd, DAY) };
};

/** Expected impressions by day (`yyyy-MM-dd`), from a summary response's `daily_stats`. */
export const dailyExpectedFromSummary = (response: unknown): Record<string, number> => {
    const dailyStats = (response as { data?: { daily_stats?: Record<string, unknown> } } | null)
        ?.data?.daily_stats;

    return Object.fromEntries(
        Object.entries(dailyStats ?? {}).map(([date, stats]) => [
            date,
            Number(
                (stats as { expected_impacts_in_schedule?: unknown })
                    ?.expected_impacts_in_schedule ?? 0,
            ) || 0,
        ]),
    );
};

/** Delivered impressions by day, from the same `daily_stats` shape. */
export const dailyImpactsFromStats = (
    dailyStats: Record<string, { impacts?: unknown } | null | undefined> | null | undefined,
): Record<string, number> =>
    Object.fromEntries(
        Object.entries(dailyStats ?? {}).map(([date, stats]) => [
            date,
            Number(stats?.impacts ?? 0) || 0,
        ]),
    );

/**
 * The last settled day: the day before yesterday. Delivery without a date range counts settled
 * rows only, so a filtered target is cut at the same day or it would run ahead of its delivery.
 */
export const lastSettledDay = (today: Date): string => format(subDays(today, 2), DAY);

type ChartRow = Record<string, unknown>;

type SeriesLabels = {
    /** The row key holding the day, `yyyy-MM-dd` (`dateLabelLabel`). */
    dateLabel: string;
    /** The row key the expected series is drawn from. */
    expectedLabel: string;
};

/**
 * Days to draw: every day the delivery rows have, plus every day the expected series has, in
 * order. The expected series runs to the campaign end (the full plan), so days after the data are
 * added with the expected figure only: delivery stops where its data stops.
 */
const mergedRows = (rows: ChartRow[], dailyExpected: Record<string, number>, dateLabel: string) => {
    const byDate = new Map<string, ChartRow>();
    rows.forEach(row => byDate.set(String(row[dateLabel]), row));
    Object.keys(dailyExpected).forEach(date => {
        if (!byDate.has(date)) byDate.set(date, { [dateLabel]: date, name: date });
    });
    return [...byDate.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
};

/** The expected line: the running total of each day's target through the campaign end. */
export const applyTargetExpectedLine = <Row extends ChartRow>(
    rows: Row[] | null,
    dailyExpected: Record<string, number> | null,
    { dateLabel, expectedLabel }: SeriesLabels,
): Row[] | null => {
    if (!rows || !dailyExpected) return rows;

    let running = 0;
    return mergedRows(rows, dailyExpected, dateLabel).map(([date, row]) => {
        running += dailyExpected[date] ?? 0;
        return { ...row, [expectedLabel]: running } as Row;
    });
};

/** The expected bars: each day's own target (0 on a day the schedule is off, e.g. weekends). */
export const applyTargetExpectedBars = <Row extends ChartRow>(
    rows: Row[] | null,
    dailyExpected: Record<string, number> | null,
    { dateLabel, expectedLabel }: SeriesLabels,
): Row[] | null => {
    if (!rows || !dailyExpected) return rows;

    return mergedRows(rows, dailyExpected, dateLabel).map(
        ([date, row]) => ({ ...row, [expectedLabel]: dailyExpected[date] ?? 0 } as Row),
    );
};

/** Running totals of the target, one per given day (for the summary sparklines). */
export const cumulativeTargetByDate = (
    dates: string[],
    dailyExpected: Record<string, number> | null,
): Record<string, number> => {
    if (!dailyExpected) return {};
    const sorted = [...dates].sort();
    const result: Record<string, number> = {};
    let running = 0;
    let cursor = 0;
    const expectedDates = Object.keys(dailyExpected).sort();
    sorted.forEach(date => {
        while (cursor < expectedDates.length && expectedDates[cursor] <= date) {
            running += dailyExpected[expectedDates[cursor]] ?? 0;
            cursor += 1;
        }
        result[date] = running;
    });
    return result;
};

/**
 * The headline target and % on a filtered view: the target on settled days inside the window,
 * and settled delivery in the same window against it.
 */
export const filteredSettledTarget = ({
    dailyExpected,
    dailyDelivered,
    lastSettled,
}: {
    dailyExpected: Record<string, number> | null;
    dailyDelivered: Record<string, number>;
    lastSettled: string;
}): SettledTarget => {
    if (!dailyExpected) return { target: null, percent: null };

    const settledSum = (byDay: Record<string, number>) =>
        Object.entries(byDay)
            .filter(([date]) => date <= lastSettled)
            .reduce((sum, [, value]) => sum + value, 0);

    const target = settledSum(dailyExpected);
    const delivered = settledSum(dailyDelivered);

    return { target, percent: target > 0 ? (delivered / target) * 100 : null };
};

/**
 * The headline for a `vistar_us2` campaign in each filter state. `'hidden'` means the filter is
 * not one targets can be split by: no target, no %, and a note saying why.
 */
export const targetHeadline = ({
    state,
    campaign,
    dailyExpected,
    dailyDelivered,
    lastSettled,
}: {
    state: TargetFilterState;
    campaign: Pick<Campaign, 'playout_stats' | 'playout_percentage'> | null | undefined;
    dailyExpected: Record<string, number> | null;
    dailyDelivered: Record<string, number>;
    lastSettled: string;
}): SettledTarget | 'hidden' => {
    if (state === 'otherFilter') return 'hidden';
    if (state === 'unfiltered') return settledTarget(campaign);
    return filteredSettledTarget({ dailyExpected, dailyDelivered, lastSettled });
};

/** What the "to date" headline box shows. */
export type ToDateHeadline = {
    /** 'target': delivery against a target; 'shareOfTotal': a filtered share, with no target. */
    mode: 'target' | 'shareOfTotal';
    /** The expected-to-date figure; null shows none. */
    expected: number | null;
    /** Delivery against `expected`, as a percentage; null shows a dash. */
    performance: number | null;
};

/**
 * The "to date" box of the DSP headline.
 *
 * Without a `targetHeadline` (every type but `vistar_us2`) it is as it always was: the even-paced
 * `campaignTarget` (or the stats' own expected), and a filtered view shows its share of the total
 * instead. With one, the figures are taken as given -- `campaign.playout_stats` and
 * `playout_percentage` unfiltered, the settled window under an insertion order / date filter --
 * and `'hidden'` shows the filtered share with no expected figure at all.
 */
export const resolveToDateHeadline = ({
    targetHeadline,
    campaignTarget,
    expectedImpressions,
    totalImpressions,
    hasActiveFilters,
}: {
    targetHeadline: SettledTarget | 'hidden' | null | undefined;
    campaignTarget: number | null | undefined;
    expectedImpressions: number;
    totalImpressions: number;
    hasActiveFilters: boolean;
}): ToDateHeadline => {
    if (targetHeadline === 'hidden') {
        return { mode: 'shareOfTotal', expected: null, performance: null };
    }
    if (targetHeadline) {
        // UL-8203: a target is never shown as 0. No live run yet (no `playout_stats`), a builder
        // that wrote 0, or a window with nothing settled all mean "no target": show none, and never
        // fall back to the stats' own expected, which is 0 on this type.
        if (!targetHeadline.target) {
            return { mode: 'target', expected: null, performance: null };
        }
        return {
            mode: 'target',
            expected: targetHeadline.target,
            performance: targetHeadline.percent,
        };
    }

    const expected = campaignTarget || expectedImpressions;
    return {
        mode: hasActiveFilters ? 'shareOfTotal' : 'target',
        expected,
        performance: expected > 0 ? (totalImpressions / expected) * 100 : null,
    };
};
